import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

import renderAccordionParagraphs from '@utils/renderAccordionParagraphs'

export const findingTheRightCardAccordions: Record<
    CreditCardProductCategory,
    { heading: string; text: React.ReactElement }[]
> = {
    [CreditCardProductCategory.BalanceTransfer]: [
        {
            heading:
                'I have multiple credit card balances and want to save on interest',
            text: renderAccordionParagraphs([
                'Standard balance transfer cards let you move existing debt and stop paying interest for a set amount of time. The longer the 0% period, the more you’ll save, but you’ll need a good credit score for the best deals.',
                'It’s also worth remembering that you can’t usually shift credit card debt to a balance transfer between the same lender or group of lenders.',
            ]),
        },

        {
            heading: 'I have a large amount of debt to transfer',
            text: renderAccordionParagraphs([
                'Look for cards with higher credit limits and longer 0% periods. TotallySure offers guarantee you’ll get the credit limit you apply for, so you know you can transfer your full balance.',
            ]),
        },

        {
            heading: 'I have a fair credit score',
            text: renderAccordionParagraphs([
                'Even with less-than-perfect credit record, you could still qualify for shorter balance transfer deals. These can still deliver significant savings compared to paying standard credit card interest rates.',
            ]),
        },

        {
            heading: 'I want to clear my overdraft',
            text: renderAccordionParagraphs([
                'Money transfer cards are similar to balance transfers but allow you to move cash directly to your bank account. You can use this to clear expensive overdrafts that might be costing you almost 50% APR.',
            ]),
        },

        {
            heading: 'I want guaranteed terms',
            text: renderAccordionParagraphs([
                'TotallySure balance transfer offers come with pre-approval and guaranteed credit limits, APRs, and 0% durations. That way you know exactly what you’ll get before you apply.',
            ]),
        },
    ],

    [CreditCardProductCategory.BestCards]: [
        {
            heading: 'I want to pay off existing debt',
            text: renderAccordionParagraphs([
                'Balance transfer cards let you move debt from other cards and stop paying interest for a set amount of time. This means you can put more of money goes towards clearing what you owe, and quicker.',
            ]),
        },

        {
            heading: 'I need to make a big purchase',
            text: renderAccordionParagraphs([
                '0% cards let you spread the cost of a purchase over several months without paying interest. It’s a good idea to work out how much you need to repay each month to clear it before the 0% period ends.',
            ]),
        },

        {
            heading: 'I want something back when I spend',
            text: renderAccordionParagraphs([
                'Cashback or rewards cards give you money or points when you spend. It’s best to only get one if you’re able to clear the balance in full every month.',
            ]),
        },

        {
            heading: 'I travel or shop abroad',
            text: renderAccordionParagraphs([
                'Travel cards can help you save you money on foreign transaction fees and give you better exchange rates. They can be good for holidays or buying from overseas websites.',
            ]),
        },

        {
            heading: 'I want to build my credit score',
            text: renderAccordionParagraphs([
                'Credit builder cards are designed for people with poor credit or no credit history. A good way to use these is for small purchases and paying it off in full each month to improve your score.',
            ]),
        },

        {
            heading: 'I want a guaranteed offer',
            text: renderAccordionParagraphs([
                'TotallySure offers come with pre-approval, guaranteed credit limits, offer lengths and APRs. That way you’ll know if you’ll be accepted, and that you’ll get exactly what you applied for.',
            ]),
        },
    ],

    [CreditCardProductCategory.Cashback]: [
        {
            heading: 'I want cashback on all my spending',
            text: renderAccordionParagraphs([
                'Flat-rate cashback cards will give you the same percentage back on everything you buy. They’re easy to understand, simpler to use, and good if you shop at different places rather than sticking to one retailer.',
            ]),
        },

        {
            heading: 'I do a big weekly shop',
            text: renderAccordionParagraphs([
                'If you’re loyal to one supermarket, their branded card might offer better cashback rates when you shop there.',
            ]),
        },

        {
            heading: 'I spend a lot each month',
            text: renderAccordionParagraphs([
                'Some cards come with tiered rates which can work well if you’re a high spender. You might earn a lower percentage on the first £10,000 you spend, and a higher rate after that. Watch out for annual fees, and remember to do the calculations to see if you’ll earn enough cashback to justify it.',
            ]),
        },

        {
            heading: 'I want simplicity',
            text: renderAccordionParagraphs([
                'If you want to keep things simple, then a flat-rate card with no caps or tiers is likely to be the easiest option. You’ll know exactly what you’re getting back on every purchase.',
            ]),
        },

        {
            heading: 'I travel or shop abroad',
            text: renderAccordionParagraphs([
                'Some cashback cards don’t charge fees when you spend abroad, which means you can earn cashback overseas without extra costs eating into what you earn. Just read the small print before you apply.',
            ]),
        },

        {
            heading: 'I want a guaranteed offer',
            text: renderAccordionParagraphs([
                'TotallySure offers come with pre-approval, guaranteed credit limits, offer lengths and APRs. That way, you know exactly what you’re getting before you apply.',
            ]),
        },
    ],

    [CreditCardProductCategory.Combined]: [
        {
            heading: 'I need to clear debt and make purchases',
            text: renderAccordionParagraphs([
                'A combo card can make life easier for you if you need to do both. You’ll just need to make sure you’re able to manage the two different 0% periods, and that you won’t be tempted to overspend.',
            ]),
        },

        {
            heading:
                'I’m mainly clearing debt but need some purchase flexibility',
            text: renderAccordionParagraphs([
                'If your focus is on clearing debt, look for cards with longer balance transfer periods. It’s also worth looking at dedicated balance transfer options as these will usually come with longer 0% offers.',
            ]),
        },

        {
            heading:
                'I want to make purchases but might need to transfer later',
            text: renderAccordionParagraphs([
                'Remember that you’ll usually need to transfer any balances within 90 days of opening the account.',
            ]),
        },

        {
            heading: 'I have multiple credit card debts',
            text: renderAccordionParagraphs([
                'Just like balance transfer cards, combo cards usually let you move debt from multiple lenders. By reducing the number of payments you’re making each month, and putting an end to interest, managing your money should be simpler. Just make sure that the credit limit covers what you need to transfer.',
            ]),
        },

        {
            heading: 'I’m worried about managing different end dates',
            text: renderAccordionParagraphs([
                'If you feel that tracking two promotional periods might be complicated, then consider a specialist card that focuses on just one thing.',
            ]),
        },

        {
            heading: 'I want a guaranteed offer',
            text: renderAccordionParagraphs([
                'TotallySure offers come with pre-approval, guaranteed credit limits, offer lengths, and APRs. That way, you’ll know exactly what you’re getting before you apply.',
            ]),
        },
    ],

    [CreditCardProductCategory.PoorCredit]: [
        {
            heading: 'I have poor credit and want to rebuild my score',
            text: renderAccordionParagraphs([
                'Credit building cards are designed for people with damaged or thin credit files. They usually have lower limits, higher rates, and no perks, but are easier to get accepted for, and can help you to prove you can manage credit responsibly.',
            ]),
        },

        {
            heading: 'I want to spread the cost of a purchase',
            text: renderAccordionParagraphs([
                '0% purchase cards let you buy things up front and pay it off each month without incurring any interest. Even with a poor credit history, some cards offer short interest-free periods on purchases, helping you manage cash flow and spread the cost.',
            ]),
        },

        {
            heading: 'I want to pay down existing credit card debt ',
            text: renderAccordionParagraphs([
                'Balance transfers let you move debt from one or more cards to another, so you can interest payments for a set amount of time. The number of months you can avoid paying interest for will depend on how good or band your credit history is.',
            ]),
        },

        {
            heading:
                'I want to clear my overdraft, or be able to borrow cash for my current account',
            text: renderAccordionParagraphs([
                'Money transfer cards are like balance transfers, but they allow you to move cash directly to your bank account. You can use the cash to clear an expensive overdraft or make purchases.',
            ]),
        },

        {
            heading: 'I want a card that rewards me for spending',
            text: renderAccordionParagraphs([
                'Reward cards will often give you cashback on your purchases, or they’ll let you earn points when you spend. With various offers around, it’s important you pick the right rewards card for your own circumstances.',
            ]),
        },
    ],

    [CreditCardProductCategory.Purchase]: [
        {
            heading: 'I need to make a large one-off purchase',
            text: renderAccordionParagraphs([
                'Standard 0% purchase cards are great for spreading the cost of bigger purchases like things like household electrical goods, home improvements, holidays or car repairs. The longer the 0% period, the longer you have to repay, and the lower your monthly payments can be.',
            ]),
        },

        {
            heading: 'I want to avoid using my overdraft',
            text: renderAccordionParagraphs([
                'Using a 0% purchase card for your regular shopping can help you to escape your overdraft, and the high interest rates they usually charge.',
            ]),
        },

        {
            heading: 'I have a fair credit score',
            text: renderAccordionParagraphs([
                'Even with a not so perfect credit score, you could still qualify for shorter 0% purchase offers. These can still help you save money compared to paying for using higher-rate credit cards or expensive overdrafts, and often come with longer repayment terms than BNPL providers.',
            ]),
        },

        {
            heading: 'I want to improve my credit score',
            text: renderAccordionParagraphs([
                'Using your 0% card responsibly, making regular purchases and paying them off in full every month will show lenders that you can manage credit responsibly. And that can help you boost your credit score.',
            ]),
        },

        {
            heading: 'I want guaranteed terms',
            text: renderAccordionParagraphs([
                'TotallySure purchase card offers come with pre-approval, guaranteed credit limits, APRs, and 0% durations. That way you’ll know exactly what you’ll get before you apply.',
            ]),
        },
    ],

    [CreditCardProductCategory.Rewards]: [
        {
            heading: 'I want cashback on everything I buy',
            text: renderAccordionParagraphs([
                'With a cashback card, you will get a percentage back of what you spend as real money – meaning no points or vouchers. These cards are usually simpler to use and more flexible, meaning they’re a great all-round option. Just keep an eye out for annual fees.',
            ]),
        },

        {
            heading: 'I do a big weekly shop at the same supermarket',
            text: renderAccordionParagraphs([
                'If you’re loyal to the same supermarket and shop there regularly, then a branded rewards card might be a good option as you can earn extra points and discounts on the things you would usually be buying anyway.',
            ]),
        },

        {
            heading: 'I travel regularly for work or holidays',
            text: renderAccordionParagraphs([
                'Air miles are points that you can usually spend on flights or hotel stays. These are often more premium cards, which come with higher annual fees, meaning they only really make sense if you spend and travel a lot.',
            ]),
        },

        {
            heading: 'I want to improve my credit score',
            text: renderAccordionParagraphs([
                'While it’s not their main purpose, making regular purchases with a rewards card, and paying them off in full every month can show lenders that you’re able to manage credit well. Just don’t fall into the trap of spending more than you can afford to pay back.',
            ]),
        },

        {
            heading: 'I spend a lot each month',
            text: renderAccordionParagraphs([
                'The rewards can really add up if you spend a lot, and pay it off in full each month – and you might even justify the annual fee on a premium card.',
            ]),
        },

        {
            heading: 'I want guaranteed terms',
            text: renderAccordionParagraphs([
                'TotallySure offers come with pre-approval, guaranteed credit limits and APRs. That way you’ll know exactly what you’ll get before you apply, with no nasty surprises.',
            ]),
        },
    ],

    [CreditCardProductCategory.Travel]: [
        {
            heading: 'I travel regularly',
            text: renderAccordionParagraphs([
                'If you’re away a few times a year, a travel card with no annual fee makes sense. You’ll save on foreign transaction fees without paying extra to pack the card.',
            ]),
        },

        {
            heading: 'I take one big holiday a year',
            text: renderAccordionParagraphs([
                'Even for one trip, a travel card can save you money. Just remember to clear the balance as soon as you’re back to avoid paying interest.',
            ]),
        },

        {
            heading: 'I need to withdraw cash abroad',
            text: renderAccordionParagraphs([
                'Look for cards that offer fee-free ATM withdrawals. Just be aware that you might pay interest from the day you take cash out, so only take out what you really need.',
            ]),
        },

        {
            heading: 'I book flights and hotels online',
            text: renderAccordionParagraphs([
                'Travel cards work for foreign websites too, and can save you money on transaction fees when you book trips or buy from overseas retailers.',
            ]),
        },

        {
            heading: 'I want Section 75 protection',
            text: renderAccordionParagraphs([
                'All credit cards give you Section 75 protection on purchases between £100-£30,000. This is especially useful for holiday bookings as you’ll get extra protection if the travel company or airline goes bust or something goes wrong.',
            ]),
        },

        {
            heading: 'I want a guaranteed offer',
            text: renderAccordionParagraphs([
                'TotallySure offers come with pre-approval, guaranteed credit limits and APRs. That way, you’ll know exactly what you’re getting before you apply.',
            ]),
        },
    ],
}
